"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import NextImage from "next/image";
import Link from "next/link";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { db, storage } from "@/firebase/config";
import { collection, query, where, limit as qLimit, onSnapshot, doc, getDoc, updateDoc } from "@/lib/firestore-instrumented";
import { fetchDiscussionDetail } from "@/lib/discussionDetailClient";
import { useAuth } from "@/context/AuthContext";
import { getAvatarUrl } from "@/lib/avatar";
import { dispatchOpenThreadPanel } from "@/lib/threadNavigation";
import { scrollFocusedDocumentTarget } from "@/lib/focusScroll.mjs";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Pencil, Share2 } from "lucide-react";
import PrimaryTabs from "@/components/PrimaryTabs";
import PostFeed from "@/components/PostFeed";
import PostCard from "@/components/PostCard";
import JoinHubButton from "@/components/JoinHubButton";
import AnimatedTabPanel from "@/components/AnimatedTabPanel";
import HubChroniclesPanel from "@/components/chronicles/HubChroniclesPanel";
import { manageHubMember as manageHubMemberRequest } from "@/lib/manageHubMember";
import { showToast } from "@/components/ToastCenter";
import SubnavBar from "@/components/layout/SubnavBar";
import { shareWithSystem } from "@/lib/nativeShare";
import LoadingState from "@/components/LoadingState";
import { usePortal } from "@/context/PortalContext";
import { getShareBranding, shareTitle } from "@/lib/portal/shareBranding.mjs";
import SingleAdSlot from "@/components/ads/SingleAdSlot";
import {
  HOUSE_PROMOTION_TEXT_MAX,
  HOUSE_PROMOTION_TEXT_MIN,
} from "@/lib/ads/housePromotion.mjs";

const ReactMarkdown = dynamic(() => import("react-markdown").then((mod) => mod.default), {
  ssr: false,
  loading: () => <div className="text-sm text-[var(--muted)]">Loading…</div>,
});
const ShareModal = dynamic(() => import("@/components/ShareModal"), {
  ssr: false,
  loading: () => null,
});

/* ---------------- skeleton ---------------- */
function HeaderSkeleton() {
  return (
    <div className="edge-mobile" aria-label="Loading space">
      <div className="square-mobile border border-[var(--border)] bg-[var(--card)] overflow-hidden">
        <div className="h-40 bg-[var(--muted)]/10" />
        <div className="p-4">
          <div className="h-5 w-40 bg-[var(--muted)]/20 rounded" />
          <div className="mt-2 h-4 w-28 bg-[var(--muted)]/20 rounded" />
          <div className="mt-3 h-16 bg-[var(--muted)]/10 rounded" />
          <LoadingState
            variant="inline"
            message="Loading space..."
            className="mt-3 text-[var(--muted)]"
          />
        </div>
      </div>
    </div>
  );
}

export default function HubClient({ slug, initialHub = null, allowedSpaceTabs = null }) {
  const [hub, setHub] = useState(initialHub); // null=loading, {}=loaded
  const [error, setError] = useState("");
  const [descOpen, setDescOpen] = useState(false);
  const [descOverflows, setDescOverflows] = useState(false);
  const [member, setMember] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);

  // NEW: holds a fetched post when it isn’t in the first page yet
  const [pinned, setPinned] = useState(null); // { threadId, data, replyId }
  const [sharedThreadId, setSharedThreadId] = useState("");

  const { user, loading } = useAuth();
  const portal = usePortal();
  const shareBranding = useMemo(() => getShareBranding(portal), [portal]);
  const userCacheRef = useRef(new Map());
  const descRef = useRef(null);
  const badgeInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const [roster, setRoster] = useState({ owners: [], members: [], agents: [], pending: [] });
  const [pendingActionId, setPendingActionId] = useState("");
  const [rosterLoading, setRosterLoading] = useState(true);
  const [editingHub, setEditingHub] = useState(false);
  const [hubDraft, setHubDraft] = useState({
    name: "",
    description: "",
    adPromotionEnabled: false,
    adPromotionText: "",
  });
  const [badgeFile, setBadgeFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [badgePreview, setBadgePreview] = useState("");
  const [bannerPreview, setBannerPreview] = useState("");
  const [savingHub, setSavingHub] = useState(false);
  const [editError, setEditError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState(null);
  const isHubOwner = useMemo(() => !!hub?.ownerUid && user?.uid === hub.ownerUid, [hub?.ownerUid, user?.uid]);
  const memberRole = (member?.role || "").toLowerCase();
  const isHubManager = isHubOwner || memberRole === "moderator";

  const status = member ? (member.status ?? "active") : "none";
  const isActiveMember = (hub?.ownerUid && user?.uid === hub.ownerUid) || status === "active";
  const canSeeTabs = hub?.type === "private" ? isActiveMember : true;
  const showJoinNotice =
    !!hub && !isActiveMember && (hub.type === "public-view" || hub.type === "public-interact");

  const rosterSections = useMemo(
    () => [
      { key: "owners", label: "Owners", people: roster.owners },
      { key: "members", label: "Members", people: roster.members },
      { key: "agents", label: "Agents", people: roster.agents },
    ],
    [roster]
  );
  const pendingMembers = roster.pending || [];
  const isRosterPending = rosterLoading;
  const hasRosterEntries = rosterSections.some((section) => section.people.length > 0);

  useEffect(() => {
    if (!hub?.description) {
      setDescOverflows(false);
      setDescOpen(false);
      return;
    }

    const el = descRef.current;
    if (!el) return;

    const measure = () => {
      const prev = el.style.cssText;

      el.style.display = "-webkit-box";
      el.style.webkitBoxOrient = "vertical";
      el.style.webkitLineClamp = "3";
      el.style.overflow = "hidden";

      const overflows = el.scrollHeight > el.clientHeight + 1;
      setDescOverflows(overflows);
      if (!overflows) setDescOpen(false);

      el.style.cssText = prev;
    };

    const raf = requestAnimationFrame(measure);

    let ro;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    } else {
      window.addEventListener("resize", measure);
    }

    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", measure);
    };
  }, [hub?.description]);

  const buildHubSharePayload = useMemo(() => {
    if (!hub?.slug) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const sharePath = `/share/space/${encodeURIComponent(hub.slug)}`;
    const url = origin ? `${origin}${sharePath}` : sharePath;
    const spaceName = hub?.name || hub?.displayName || toTitle(hub.slug);
    const desc = String(hub?.description || "").replace(/\s+/g, " ").trim();
    const snippet = desc ? (desc.length > 140 ? `${desc.slice(0, 140).trim()}…` : desc) : `Check out this space on ${shareBranding.siteName}.`;
    return {
      url,
      title: shareTitle(spaceName, shareBranding),
      text: `${snippet}\n\n— +${hub.slug} on ${shareBranding.siteName}`,
    };
  }, [hub?.slug, hub?.name, hub?.displayName, hub?.description, shareBranding]);

  const handleHubShare = useCallback(async (event) => {
    event?.stopPropagation?.();
    if (!buildHubSharePayload) return;
    if (await shareWithSystem(buildHubSharePayload)) return;

    setSharePayload(buildHubSharePayload);
    setShareOpen(true);
  }, [buildHubSharePayload]);

  useEffect(() => {
    if (!hub) return;
    setHubDraft({
      name: hub.displayName || hub.name || toTitle(hub.slug),
      description: hub.description || "",
      adPromotionEnabled: hub.adPromotion?.enabled === true,
      adPromotionText: hub.adPromotion?.text || "",
    });
  }, [hub?.slug, hub?.displayName, hub?.name, hub?.description, hub?.adPromotion]);

  useEffect(() => {
    return () => {
      if (badgePreview) URL.revokeObjectURL(badgePreview);
      if (bannerPreview) URL.revokeObjectURL(bannerPreview);
    };
  }, [badgePreview, bannerPreview]);

  const resetPreview = (type = "badge") => {
    if (type === "badge") {
      if (badgePreview) URL.revokeObjectURL(badgePreview);
      setBadgePreview("");
      setBadgeFile(null);
      if (badgeInputRef.current) badgeInputRef.current.value = "";
    } else {
      if (bannerPreview) URL.revokeObjectURL(bannerPreview);
      setBannerPreview("");
      setBannerFile(null);
      if (bannerInputRef.current) bannerInputRef.current.value = "";
    }
  };

  const startHubEdit = () => {
    if (!hub) return;
    setEditError("");
    setDescOpen(false);
    resetPreview("badge");
    resetPreview("banner");
    setEditingHub(true);
  };

  const cancelHubEdit = () => {
    setEditError("");
    setSavingHub(false);
    resetPreview("badge");
    resetPreview("banner");
    setEditingHub(false);
  };

  const handleBadgeInput = (event) => {
    const file = event.target.files?.[0] || null;
    resetPreview("badge");
    if (file) {
      const url = URL.createObjectURL(file);
      setBadgeFile(file);
      setBadgePreview(url);
    }
  };

  const handleBannerInput = (event) => {
    const file = event.target.files?.[0] || null;
    resetPreview("banner");
    if (file) {
      const url = URL.createObjectURL(file);
      setBannerFile(file);
      setBannerPreview(url);
    }
  };

  const handleSaveHub = async (event) => {
    event?.preventDefault?.();
    if (!hub?.slug) return;
    const trimmedName = hubDraft.name.trim();
    if (!trimmedName) {
      setEditError("Space name is required.");
      return;
    }
    const promotionText = hubDraft.adPromotionText.replace(/\s+/g, " ").trim();
    if (hubDraft.adPromotionEnabled) {
      if (!["public-view", "public-interact"].includes(hub.type) || hub.publicReadable === false) {
        setEditError("Only public spaces can participate in Featured Space promotions.");
        return;
      }
      if (
        promotionText.length < HOUSE_PROMOTION_TEXT_MIN ||
        promotionText.length > HOUSE_PROMOTION_TEXT_MAX
      ) {
        setEditError(
          `Promotional copy must be ${HOUSE_PROMOTION_TEXT_MIN}-${HOUSE_PROMOTION_TEXT_MAX} characters.`
        );
        return;
      }
      if (!(badgeFile || hub.badgeUrl) || !(bannerFile || hub.bannerUrl)) {
        setEditError("A badge and banner are required to participate in Featured Space promotions.");
        return;
      }
    }
    setSavingHub(true);
    setEditError("");
    try {
      const hubRef = doc(db, "hubs", hub.slug);
      const updates = {
        displayName: trimmedName,
        name: trimmedName,
        description: hubDraft.description.trim(),
        adPromotion: {
          enabled: hubDraft.adPromotionEnabled,
          text: promotionText,
        },
        updatedAt: new Date(),
      };

      if (badgeFile) {
        const ext = extFromType(badgeFile.type);
        const storageRef = ref(storage, `hubs/${hub.slug}/badge.${ext}`);
        await uploadBytes(storageRef, badgeFile, {
          contentType: badgeFile.type || "image/*",
          cacheControl: "public,max-age=86400",
        });
        updates.badgeUrl = await getDownloadURL(storageRef);
      }

      if (bannerFile) {
        const ext = extFromType(bannerFile.type);
        const storageRef = ref(storage, `hubs/${hub.slug}/banner.${ext}`);
        await uploadBytes(storageRef, bannerFile, {
          contentType: bannerFile.type || "image/*",
          cacheControl: "public,max-age=86400",
        });
        updates.bannerUrl = await getDownloadURL(storageRef);
      }

      await updateDoc(hubRef, updates);
      setHub((prev) => ({
        ...(prev || {}),
        ...updates,
      }));
      cancelHubEdit();
    } catch (err) {
      setEditError(err.message || "Could not update space.");
    } finally {
      setSavingHub(false);
    }
  };

  /* ---------------- load hub by slug ---------------- */
  useEffect(() => {
    const qHub = query(collection(db, "hubs"), where("slug", "==", slug), qLimit(1));
    const unsub = onSnapshot(
      qHub,
      (snap) => {
        const doc0 = snap.docs[0];
        setHub(doc0 ? { id: doc0.id, ...doc0.data() } : {});
        setError("");
      },
      (e) => {
        console.error("HubClient hub load error:", e);
        setError("Could not load space.");
        setHub({});
      }
    );
    return () => unsub();
  }, [slug]);

  /* ---------------- membership check ---------------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user || !hub?.slug) {
        if (mounted) {
          setMember(null);
          setMemberLoading(false);
        }
        return;
      }
      setMemberLoading(true);
      try {
        const memberId = `${hub.slug}_${user.uid}`;
        const snap = await withTimeout(
          getDoc(doc(db, "hubMembers", memberId)),
          5000,
          "Membership lookup timed out."
        );
        if (!mounted) return;
        setMember(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (memberError) {
        console.warn("HubClient membership load failed:", memberError);
        if (mounted) setMember(null);
      } finally {
        if (mounted) setMemberLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user, hub?.slug]);

  useEffect(() => {
    if (!hub?.slug) {
      setRoster({ owners: [], members: [], agents: [], pending: [] });
      setRosterLoading(false);
      return;
    }
    let active = true;
    setRosterLoading(true);
    const qMembers = query(collection(db, "hubMembers"), where("hubId", "==", hub.slug));
    const unsub = onSnapshot(
      qMembers,
      (snap) => {
        (async () => {
          const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          const uids = Array.from(new Set(rows.map((row) => row.uid).filter(Boolean)));
          const cache = userCacheRef.current;
          const missing = uids.filter((uid) => !cache.has(uid));
          if (missing.length) {
            const fetched = await Promise.all(
              missing.map(async (uid) => {
                try {
                  const userSnap = await getDoc(doc(db, "users", uid));
                  return { uid, data: userSnap.exists() ? userSnap.data() : null };
                } catch (err) {
                  console.warn("HubClient member profile fetch failed:", err);
                  return { uid, data: null };
                }
              })
            );
            fetched.forEach(({ uid, data }) => {
              cache.set(uid, data);
            });
          }
          const owners = [];
          const membersList = [];
          const agentsList = [];
          const pendingList = [];
          const syntheticAgentIds = new Set(
            rows
              .map((row) => row.syntheticSourceAgentId || (String(row.uid || "").startsWith("synthetic:") ? String(row.uid).slice("synthetic:".length) : ""))
              .filter(Boolean)
          );
          rows.forEach((row) => {
            const role = (row.role || "").toLowerCase();
            const rowAgentId = row.agentId || (String(row.uid || "").startsWith("agent:") ? String(row.uid).slice("agent:".length) : "");
            if (role === "agent" && rowAgentId && syntheticAgentIds.has(rowAgentId)) {
              return;
            }
            const statusValue = (row.status || "active").toLowerCase();
            const profileOverride =
              role === "agent"
                ? {
                    displayName: row.agentDisplayName || row.displayName || row.name || "",
                    username: row.agentHandle || row.handle || "",
                    username_lowercase: (row.agentHandle || row.handle || "").toLowerCase(),
                    profilePictureUrl: row.agentAvatarUrl || row.avatarUrl || "",
                  }
                : null;
            const profile = row.uid ? cache.get(row.uid) : null;
            const shaped = shapeMemberEntry(row, profile, profileOverride);
            if (!shaped) return;
            shaped.role = role || "member";
            shaped.profileHref =
              role === "agent"
                ? `/agent/${encodeURIComponent(shaped.handle)}`
                : `/u/${encodeURIComponent(shaped.handle)}`;
            if (role === "owner") {
              if (statusValue === "active") owners.push(shaped);
            } else if (role === "agent") {
              agentsList.push(shaped);
            } else {
              if (statusValue === "active") membersList.push(shaped);
              else pendingList.push(shaped);
            }
          });
          if (!active) return;
          setRoster({ owners, members: membersList, agents: agentsList, pending: pendingList });
          setRosterLoading(false);
        })().catch((err) => {
          console.warn("HubClient member roster error:", err);
          if (!active) return;
          setRoster({ owners: [], members: [], agents: [], pending: [] });
          setRosterLoading(false);
        });
      },
      (err) => {
        console.error("[HubClient] hubMembers listener failed:", err);
        if (!active) return;
        setRosterLoading(false);
      }
    );
    return () => {
      active = false;
      unsub();
    };
  }, [hub?.slug]);

  /* ---------------- tabs ---------------- */
  const tabs = useMemo(
    () => [
      { key: "discussions", label: "Discussions" },
      { key: "chronicles", label: "Chronicles" },
      { key: "members", label: "Members" },
      { key: "info", label: "Info" },
    ].filter((tab) => !Array.isArray(allowedSpaceTabs) || allowedSpaceTabs.includes(tab.key)),
    [allowedSpaceTabs]
  );

  /* ---------------------------------------------------------------------
     Deep-link focus: /h/{slug}?focus=thread:{threadId}&replyId=...
     - First, ask PostFeed to await and scroll when it renders that thread.
     - If not found soon after, fetch and pin the post above the feed.
     - Clean the URL once handled to keep history tidy.
     --------------------------------------------------------------------- */
  const search = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const tabFromUrl = search.get("t");
  const normalizedActive = normalizeHubTab(tabFromUrl);
  const active = tabs.some((tab) => tab.key === normalizedActive)
    ? normalizedActive
    : tabs[0]?.key || "discussions";

  useEffect(() => {
    const focus = search.get("focus"); // e.g. "thread:abc123"
    if (!focus || !hub?.slug || loading) return;
    if (!user && hub.type === "private") {
      const queryString = search.toString();
      const dest = `${pathname}${queryString ? `?${queryString}` : ""}`;
      router.replace(`/login?redirect=${encodeURIComponent(dest)}`);
      return;
    }
  }, [search, hub?.slug, hub?.type, user, loading, pathname, router]);

  useEffect(() => {
    if (tabFromUrl && normalizeHubTab(tabFromUrl) !== tabFromUrl) {
      const params = new URLSearchParams(search.toString());
      params.delete("t");
      const nextQuery = params.toString();
      router.replace(`${pathname}${nextQuery ? `?${nextQuery}` : ""}`, { scroll: false });
    }
  }, [tabFromUrl, pathname, router, search]);

  const badgeLetter = ((hubDraft.name || hub?.slug || "").trim()[0] || "S").toUpperCase();
  const currentBadgePreview = badgePreview || hub?.badgeUrl || "";
  const currentBannerPreview = bannerPreview || hub?.bannerUrl || "";
  const handleMemberAction = async (person, action) => {
    if (!person?.id || !hub?.slug) return;
    if (!user) {
      showToast("Sign in to manage members.");
      return;
    }
    const marker = `${action}:${person.docId || person.id}`;
    setPendingActionId(marker);
    try {
      const idToken = await user.getIdToken(true);
      await manageHubMemberRequest({
        idToken,
        hubSlug: hub.slug,
        memberUid: person.uid || person.id,
        memberDocId: person.docId,
        action,
      });
      const success = {
        approve: "Member approved.",
        reject: "Request rejected.",
        remove: "Member removed.",
      };
      if (success[action]) {
        showToast(success[action]);
      }
    } catch (err) {
      console.error(`[HubClient] ${action} member failed:`, err);
      showToast(err?.message || `Could not ${action} member.`);
    } finally {
      setPendingActionId("");
    }
  };

  useEffect(() => {
    const focus = search.get("focus"); // e.g. "thread:abc123"
    const replyId = search.get("replyId") || null;
    const shareEntry = search.get("entry") === "share";
    if (!focus || !hub?.slug) return;
    if (!user && !loading && hub.type === "private") return;

    const [kind, threadId] = String(focus).split(":");
    if (kind !== "thread" || !threadId) return;
    const shouldShowShareAd =
      shareEntry &&
      !replyId &&
      ["public-view", "public-interact"].includes(hub.type);
    setSharedThreadId(shouldShowShareAd ? threadId : "");

    const clearFocusUrl = () => {
      window.history.replaceState(window.history.state, "", pathname);
    };

    // Tell PostFeed to scroll when/if the thread shows up in its rows
    // (PostFeed listens for "post:await" and scrolls to #thread-{id}). :contentReference[oaicite:2]{index=2}
    window.dispatchEvent(new CustomEvent("post:await", { detail: { threadId } }));

    const dispatchOpenPanel = () => {
      if (!replyId) return;
      dispatchOpenThreadPanel({
        hubSlug: hub.slug,
        threadId,
        replyId: replyId || null,
      });
    };

    // After a brief wait (let first page render), pin if not present.
    const t = setTimeout(async () => {
      const el = document.getElementById(`thread-${threadId}`); // set by PostCard. :contentReference[oaicite:3]{index=3}
      if (el) {
        scrollFocusedDocumentTarget(el);
        if (replyId) setTimeout(dispatchOpenPanel, 200);
        clearFocusUrl();
        return;
      }

      try {
        const ref = doc(db, `discussions/${hub.slug}/threads/${threadId}`);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          let author = null;
          if (data.authorUid) {
            try {
              const authorSnap = await getDoc(doc(db, "users", data.authorUid));
              if (authorSnap.exists()) {
                const u = authorSnap.data() || {};
                author = {
                  uid: data.authorUid,
                  displayName: u.displayName || "Member",
                  username: u.username || u.username_lowercase || "",
                  username_lowercase: u.username_lowercase || u.username || "",
                  profilePictureUrl: u.profilePictureUrl || "",
                };
              }
            } catch (authorErr) {
              console.debug("Pinned author fetch failed", authorErr);
            }
          }

          setPinned({
            threadId,
            replyId,
            data: {
              id: snap.id,
              hubSlug: hub.slug,
              ...data,
              ...(author ? { author } : {}),
            },
          });
          if (replyId) setTimeout(dispatchOpenPanel, 200);
          clearFocusUrl();
          return;
        }
        const detail = await fetchDiscussionDetail({ hubSlug: hub.slug, threadId, user });
        setPinned({
          threadId,
          replyId,
          data: {
            ...detail.thread,
            hubSlug: hub.slug,
            ...(detail.author ? { author: detail.author } : {}),
          },
        });
        if (replyId) setTimeout(dispatchOpenPanel, 200);
        clearFocusUrl();
      } catch (e) {
        console.warn("Focus fallback fetch failed", e);
      }
    }, 500);

    return () => {
      clearTimeout(t);
    };
  }, [search, hub?.slug, hub?.type, user, loading, pathname]);

  const activePanel = useMemo(() => {
    if (!canSeeTabs) return null;
    if (active === "discussions" && hub?.slug) {
      return (
        <div className="space-y-1">
          {pinned && (
            <>
              <div className="edge-mobile">
                <div className="card square-mobile border border-[var(--border)] bg-[var(--card)]">
                  <PostCard t={pinned.data} hubSlug={hub.slug} clampLines={6} focused />
                  <div className="flex justify-between border-t border-[var(--border)] px-4 py-3 text-xs text-[var(--muted)] sm:px-6">
                    <span>Focused post {pinned.replyId ? `(reply ${pinned.replyId})` : ""}</span>
                    <button
                      type="button"
                      className="font-semibold text-[var(--brand)] hover:underline"
                      onClick={() => {
                        setPinned(null);
                        setSharedThreadId("");
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
              {sharedThreadId === pinned.threadId ? (
                <SingleAdSlot placementId="share.thread.after" />
              ) : null}
            </>
          )}

          <PostFeed
            scope="hub"
            hubSlug={hub.slug}
            hubType={hub.type}
            isMember={isActiveMember}
            db={db}
            pageSize={20}
            shareThreadId={pinned ? "" : sharedThreadId}
          />
        </div>
      );
    }

    if (active === "chronicles" && hub?.slug) {
      return (
        <HubChroniclesPanel
          hubSlug={hub.slug}
          hubDocId={hub.id || ""}
          user={user}
          canCreate={isActiveMember}
        />
      );
    }

    if (active === "members") {
      return (
        <div className="space-y-1">
          {isRosterPending
            ? [0, 1, 2].map((idx) => (
                <div key={idx} className="edge-mobile">
                  <section className="card square-mobile border border-[var(--border)] bg-[var(--card)] px-4 py-6 sm:px-6">
                    <div className="h-5 w-32 animate-pulse rounded-sm bg-[var(--surface)]" />
                    <div className="mt-4 space-y-2">
                      {[0, 1].map((row) => (
                        <div key={row} className="h-12 animate-pulse rounded-sm bg-[var(--surface-2)]" />
                      ))}
                    </div>
                  </section>
                </div>
              ))
            : rosterSections.map((section) => (
                <div key={section.key} className="edge-mobile">
                  <section className="card square-mobile border border-[var(--border)] bg-[var(--card)] px-0 py-0">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] px-4 py-3 sm:px-6">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
                          {section.label}
                        </p>
                        <h5 className="text-lg font-semibold text-[var(--fg-primary)]">
                          {section.people.length} {section.people.length === 1 ? "person" : "people"}
                        </h5>
                      </div>
                    </div>
                    <div className="divide-y divide-[var(--border)]">
                      {section.people.length === 0 ? (
                        <p className="px-4 py-6 text-sm text-[var(--muted)] sm:px-6">
                          No {section.label.toLowerCase()} listed yet.
                        </p>
                      ) : (
                        section.people.map((person) => (
                          <RosterRow
                            key={person.id}
                            person={person}
                            showRemove={section.key === "members" && isHubManager}
                            onRemove={() => handleMemberAction(person, "remove")}
                            removing={pendingActionId === `remove:${person.docId || person.id}`}
                          />
                        ))
                      )}
                    </div>
                  </section>
                </div>
              ))}

          {!isRosterPending && !hasRosterEntries && (
            <div className="edge-mobile">
              <section className="card square-mobile border border-dashed border-[var(--border)] bg-[var(--card)] px-4 py-6 text-center text-sm text-[var(--muted)] sm:px-6">
                No members or agents yet. Invite your community to get things started.
              </section>
            </div>
          )}
        </div>
      );
    }

    if (active === "info") {
      return (
        <div className="edge-mobile">
          <section className="card square-mobile border border-[var(--border)] bg-[var(--card)] px-4 py-6 sm:px-6">
            <h4 className="font-semibold">About this space</h4>
            <div className="mt-2 text-[15px] leading-6">
              <ReactMarkdown>{hub?.description || "_No description yet._"}</ReactMarkdown>
            </div>
          </section>
        </div>
      );
    }

    return null;
  }, [
    canSeeTabs,
    active,
    hub?.slug,
    hub?.type,
    hub?.description,
    pinned,
    sharedThreadId,
    user,
    isActiveMember,
    isRosterPending,
    rosterSections,
    hasRosterEntries,
    isHubOwner,
    isHubManager,
  ]);

  return (
    <main className="relative z-0 mx-auto max-w-3xl px-4 pt-0 pb-24">
      {/* Sticky tabs */}
      {canSeeTabs && (
        <SubnavBar>
          <PrimaryTabs
            tabs={tabs}
            active={active}
            onChange={(nextTab) => {
              const params = new URLSearchParams(search.toString());
              if (nextTab === "discussions") params.delete("t");
              else params.set("t", nextTab);
              const nextQuery = params.toString();
              router.replace(`${pathname}${nextQuery ? `?${nextQuery}` : ""}`, { scroll: false });
            }}
          />
        </SubnavBar>
      )}

      {/* SpaceHeader */}
      {!hub ? (
        <HeaderSkeleton />
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
            <div className="edge-mobile mt-2">
              {editingHub ? (
            <section className="card square-mobile border border-[var(--border)] bg-[var(--card)] p-6">
              <form className="space-y-6" onSubmit={handleSaveHub}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--fg-primary)]">Edit space</h2>
                    <p className="text-sm text-[var(--muted)]">Updates are visible immediately.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={cancelHubEdit}
                      className="rounded-[var(--radius-sm)] border border-[var(--border)] px-3 py-1.5 text-sm text-[var(--fg-primary)] hover:bg-[var(--surface-2)]"
                      disabled={savingHub}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={savingHub}
                      className="rounded-[var(--radius)] bg-[var(--brand)] px-4 py-2 text-sm font-medium text-[var(--brand-foreground)] disabled:opacity-60"
                    >
                      {savingHub ? "Saving…" : "Save Changes"}
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="block text-sm font-medium">Space name</label>
                    <input
                      value={hubDraft.name}
                      onChange={(e) => setHubDraft((prev) => ({ ...prev, name: e.target.value }))}
                      className="input"
                      required
                      maxLength={80}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium">Description</label>
                    <textarea
                      value={hubDraft.description}
                      onChange={(e) => setHubDraft((prev) => ({ ...prev, description: e.target.value }))}
                      className="input min-h-[120px]"
                      rows={4}
                    />
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium">Badge</label>
                      <div className="flex items-center gap-3">
                        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-2)] text-2xl font-semibold text-white">
                          {currentBadgePreview ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img src={currentBadgePreview} alt="Badge preview" className="h-full w-full object-cover" />
                          ) : (
                            <span>{badgeLetter}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="rounded-[var(--radius-sm)] border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--fg-primary)] hover:bg-[var(--surface-2)]"
                            onClick={() => badgeInputRef.current?.click()}
                          >
                            Upload
                          </button>
                          {badgeFile ? (
                            <button
                              type="button"
                              className="rounded-[var(--radius-sm)] border border-[var(--border)] px-3 py-1.5 text-xs text-[var(--muted)] hover:bg-[var(--surface-2)]"
                              onClick={() => resetPreview("badge")}
                            >
                              Clear
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <input
                        ref={badgeInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        onChange={handleBadgeInput}
                      />
                      <p className="text-xs text-[var(--muted)]">PNG, JPG, or WebP. Ideal size 256×256.</p>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium">Banner</label>
                      <div className="h-24 w-full overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface-2)]">
                        {currentBannerPreview ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={currentBannerPreview} alt="Banner preview" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-sm text-[var(--muted)]">
                            No banner uploaded
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded-[var(--radius-sm)] border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--fg-primary)] hover:bg-[var(--surface-2)]"
                          onClick={() => bannerInputRef.current?.click()}
                        >
                          Upload
                        </button>
                        {bannerFile ? (
                          <button
                            type="button"
                            className="rounded-[var(--radius-sm)] border border-[var(--border)] px-3 py-1.5 text-xs text-[var(--muted)] hover:bg-[var(--surface-2)]"
                            onClick={() => resetPreview("banner")}
                          >
                            Clear
                          </button>
                        ) : null}
                      </div>
                      <input
                        ref={bannerInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        onChange={handleBannerInput}
                      />
                      <p className="text-xs text-[var(--muted)]">Wide images (1200×300+) look best.</p>
                    </div>
                  </div>

                  <div className="border-t border-[var(--border)] pt-5">
                    <div className="flex items-start gap-3">
                      <input
                        id="featured-space-participation"
                        type="checkbox"
                        checked={hubDraft.adPromotionEnabled}
                        onChange={(event) => setHubDraft((prev) => ({
                          ...prev,
                          adPromotionEnabled: event.target.checked,
                        }))}
                        className="mt-1 h-4 w-4 accent-[var(--brand)]"
                      />
                      <div className="min-w-0 flex-1">
                        <label
                          htmlFor="featured-space-participation"
                          className="block text-sm font-semibold text-[var(--fg-primary)]"
                        >
                          Participate in Featured Space promotions
                        </label>
                        <p className="mt-1 text-sm text-[var(--muted)]">
                          Allow Sproutly to promote this public space using its name, badge, banner, and approved copy.
                        </p>
                      </div>
                    </div>

                    {hubDraft.adPromotionEnabled ? (
                      <div className="mt-4 space-y-1">
                        <label htmlFor="featured-space-copy" className="block text-sm font-medium">
                          Promotional copy
                        </label>
                        <textarea
                          id="featured-space-copy"
                          value={hubDraft.adPromotionText}
                          onChange={(event) => setHubDraft((prev) => ({
                            ...prev,
                            adPromotionText: event.target.value,
                          }))}
                          className="input min-h-[96px]"
                          rows={3}
                          minLength={HOUSE_PROMOTION_TEXT_MIN}
                          maxLength={HOUSE_PROMOTION_TEXT_MAX}
                          required
                        />
                        <div className="flex items-center justify-between gap-4 text-xs text-[var(--muted)]">
                          <span>Shown beneath the space name when it is featured.</span>
                          <span>{hubDraft.adPromotionText.length}/{HOUSE_PROMOTION_TEXT_MAX}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {editError ? <p className="text-sm text-red-600">{editError}</p> : null}
                </div>
              </form>
            </section>
          ) : (
            <div className="square-mobile border border-[var(--border)] bg-[var(--card)] overflow-hidden">
              <div className="relative">
                {hub.bannerUrl ? (
                  <div className="relative h-40 w-full">
                    <NextImage
                      src={hub.bannerUrl}
                      alt={`${hub.name || hub.displayName || hub.slug} banner`}
                      fill
                      unoptimized
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 1000px"
                      priority
                    />
                  </div>
                ) : (
                  <div
                    aria-hidden
                    className="h-40 w-full"
                    style={{ background: "linear-gradient(135deg, rgba(124,59,237,.25), rgba(124,59,237,.1))" }}
                  />
                )}
                <div className="absolute left-4 -bottom-6 h-16 w-16 overflow-hidden rounded-2xl ring-2 ring-[var(--card)] shadow">
                  {hub.badgeUrl ? (
                    <div className="relative h-full w-full">
                      <NextImage
                        src={hub.badgeUrl}
                        alt={`${hub.name || hub.displayName || hub.slug} badge`}
                        fill
                        className="object-cover"
                        sizes="64px"
                        priority
                      />
                    </div>
                  ) : (
                    <div
                      aria-hidden
                      className="h-full w-full"
                      style={{ background: "linear-gradient(135deg, rgba(124,59,237,.35), rgba(124,59,237,.15))" }}
                    />
                  )}
                </div>
              </div>

              <div className="px-4 pt-8 pb-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xl font-semibold truncate">
                      {hub.name || hub.displayName || toTitle(hub.slug)}
                    </div>
                    <div className="text-sm text-[var(--muted)] truncate">+{hub.slug}</div>
                  </div>
                  <div className="shrink-0">
                    <JoinHubButton
                      hubSlug={hub.slug}
                      ownerUid={hub.ownerUid}
                      member={member}
                      membershipLoading={memberLoading}
                      onMembershipChange={setMember}
                    />
                  </div>
                </div>

                {hub.description ? (
                  <div ref={descRef} className={`mt-2 text-[15px] leading-6 ${descOpen ? "" : "line-clamp-3"}`}>
                    <ReactMarkdown>{hub.description}</ReactMarkdown>
                  </div>
                ) : null}

                <div className="mt-3 flex min-h-8 items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleHubShare}
                      className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-3 py-1 text-xs font-medium text-[var(--muted)] hover:text-[var(--fg-primary)]"
                      aria-label="Share space"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      Share
                    </button>
                    {isHubOwner && (
                      <button
                        type="button"
                        onClick={startHubEdit}
                        className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-3 py-1 text-xs font-medium text-[var(--muted)] hover:text-[var(--fg-primary)]"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    )}
                  </div>
                  {descOverflows ? (
                    <button
                      type="button"
                      onClick={() => setDescOpen((v) => !v)}
                      className="shrink-0 text-sm font-medium text-[var(--brand)] hover:underline"
                    >
                      {descOpen ? "Less" : "More"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {isHubManager && pendingMembers.length > 0 && (
        <div className="edge-mobile mt-2">
          <section className="card square-mobile border border-[var(--border)] bg-[var(--card)]">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] px-4 py-3 sm:px-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">Pending members</p>
                <h5 className="text-lg font-semibold text-[var(--fg-primary)]">
                  {pendingMembers.length} {pendingMembers.length === 1 ? "request" : "requests"}
                </h5>
              </div>
            </div>
            <div className="divide-y divide-[var(--border)]">
              {pendingMembers.map((person) => (
                <PendingRow
                  key={person.docId || person.id}
                  person={person}
                  approving={pendingActionId === `approve:${person.docId || person.id}`}
                  rejecting={pendingActionId === `reject:${person.docId || person.id}`}
                  onApprove={() => handleMemberAction(person, "approve")}
                  onReject={() => handleMemberAction(person, "reject")}
                />
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Notices */}
      {hub?.type === "private" && !isActiveMember && (
        <div className="mt-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--muted)]/10 px-3 py-2 text-sm">
          <span className="font-medium">This space is private.</span> Request access to view posts.
        </div>
      )}
      {showJoinNotice && (
        <div className="mt-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--muted)]/10 px-3 py-2 text-sm">
          <span className="font-medium">
            {hub.type === "public-view" ? "Join to post & get updates." : "Join to follow & get updates."}
          </span>
        </div>
      )}

      {/* Panels */}
      {canSeeTabs && (
        <AnimatedTabPanel activeKey={active} className="mt-2">
          {activePanel}
        </AnimatedTabPanel>
      )}

      {shareOpen ? (
        <ShareModal
          open={shareOpen}
          onClose={() => {
            setShareOpen(false);
            setSharePayload(null);
          }}
          payload={sharePayload}
        />
      ) : null}
    </main>
  );
}

function withTimeout(promise, timeoutMs, message) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function RosterRow({ person, showRemove, onRemove, removing }) {
  const profileHref = person.profileHref || `/u/${encodeURIComponent(person.handle || person.id || "member")}`;
  return (
    <div className="flex flex-wrap items-center gap-4 px-4 py-3 sm:px-6">
      <Link
        href={profileHref}
        className="group flex min-w-[220px] flex-1 items-center gap-4 rounded-[var(--radius-sm)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--card)]"
      >
        <img
          src={person.avatar}
          alt={`${person.name} avatar`}
          className="h-12 w-12 rounded-full border border-[var(--border)] bg-[var(--card)] object-cover transition group-hover:border-[var(--brand)]"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-[var(--foreground)] group-hover:text-[var(--brand)]">{person.name}</span>
            <span className="text-sm text-[var(--muted)]">@{person.handle}</span>
          </div>
          <p className="text-xs text-[var(--muted)]">Member since {formatMemberSince(person.since)}</p>
        </div>
      </Link>
      {showRemove && (
        <button
          type="button"
          disabled={removing}
          onClick={onRemove}
          className="ml-auto rounded-full border border-[var(--border)] px-3 py-1 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]/10 disabled:opacity-50"
        >
          {removing ? "Removing…" : "Remove"}
        </button>
      )}
    </div>
  );
}

/* ---------------- helpers ---------------- */
function toTitle(slug = "") {
  return String(slug)
    .split("-")
    .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : ""))
    .join(" ");
}

function normalizeHubTab(tab) {
  if (tab === "chronicles" || tab === "members" || tab === "info") return tab;
  return "discussions";
}

function extFromType(type = "") {
  const normalized = type.toLowerCase();
  if (normalized.includes("png")) return "png";
  if (normalized.includes("gif")) return "gif";
  if (normalized.includes("webp")) return "webp";
  return "jpg";
}

function shapeMemberEntry(row, profile, overrideProfile = null) {
  const uid = row.uid || row.id;
  if (!uid && !profile) return null;
  const sourceProfile = profile || overrideProfile;
  const handleSource =
    sourceProfile?.username_lowercase ||
    sourceProfile?.username ||
    (uid ? uid.replace(/^agent:/, "").slice(0, 12) : "member");
  const normalizedHandle = String(handleSource || "member").replace(/^@/, "");
  const displayName =
    sourceProfile?.displayName || sourceProfile?.username || row.displayName || normalizedHandle;
  const avatarProfile =
    sourceProfile && typeof sourceProfile === "object"
      ? { ...sourceProfile }
      : { username_lowercase: normalizedHandle, profilePictureUrl: "" };
  const avatar = getAvatarUrl({ profile: avatarProfile });
  return {
    id: uid || row.id || normalizedHandle,
    uid: row.uid || null,
    docId: row.id,
    name: displayName,
    handle: normalizedHandle,
    avatar,
    since: row.joinedAt || profile?.createdAt || null,
  };
}

function PendingRow({ person, approving, rejecting, onApprove, onReject }) {
  return (
    <div className="flex flex-wrap items-center gap-4 px-4 py-3 sm:px-6">
      <img
        src={person.avatar}
        alt={`${person.name} avatar`}
        className="h-12 w-12 rounded-full border border-[var(--border)] bg-[var(--card)] object-cover"
      />
      <div className="min-w-[200px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-[var(--foreground)]">{person.name}</span>
          <span className="text-sm text-[var(--muted)]">@{person.handle}</span>
        </div>
        <p className="text-xs text-[var(--muted)]">Awaiting approval</p>
      </div>
      <div className="ml-auto flex gap-2">
        <button
          type="button"
          disabled={rejecting}
          onClick={onReject}
          className="rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          {rejecting ? "Rejecting…" : "Reject"}
        </button>
        <button
          type="button"
          disabled={approving}
          onClick={onApprove}
          className="rounded-full border border-[var(--border)] px-3 py-1 text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]/10 disabled:opacity-50"
        >
          {approving ? "Accepting…" : "Accept"}
        </button>
      </div>
    </div>
  );
}

function formatMemberSince(value) {
  const date = toDateSafe(value);
  if (!date) return "—";
  return date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function toDateSafe(value) {
  if (!value) return null;
  try {
    if (typeof value.toDate === "function") return value.toDate();
  } catch {
    // fall through
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
